import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import apiService from '../../services/apiService';
import { Settings, Play, Pause, Activity, ShieldCheck, Database, X, FileText } from 'lucide-react';

const SeoHealthConsole = ({ domainId }) => {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingConfig, setEditingConfig] = useState(null);
    const [editBatchSize, setEditBatchSize] = useState(50);
    const [startingJobId, setStartingJobId] = useState(null);

    const [reportConfigId, setReportConfigId] = useState(null);
    const [katifiReport, setKatifiReport] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);

    const openReport = async (configId) => {
        setReportConfigId(configId);
        setLoadingReport(true);
        try {
            const data = await apiService.makeRequest(`/organic-seo/maintenance/config/${configId}/katifi-report`);
            setKatifiReport(data);
        } catch (e) {
            console.error("Katifi Report error", e);
        } finally {
            setLoadingReport(false);
        }
    };

    const fetchConfigs = async () => {
        if (!domainId) return;
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/organic-seo/maintenance/config?domain_id=${domainId}`);
            setConfigs(data);
        } catch (error) {
            console.error("Error fetching SEO maintenance configs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigs();
    }, [domainId]);

    const startJob = async (config) => {
        if (startingJobId === config.id) return;
        setStartingJobId(config.id);
        try {
            await apiService.makeRequest(`/organic-seo/maintenance/config/${config.id}/start`, { method: 'POST' });
            fetchConfigs();
        } catch (error) {
            console.error("Error starting job", error);
        } finally {
            setStartingJobId(null);
        }
    };

    const stopJob = async (config) => {
        try {
            await apiService.makeRequest(`/organic-seo/maintenance/config/${config.id}/stop`, { method: 'POST' });
            fetchConfigs();
        } catch (error) {
            console.error("Error stopping job", error);
        }
    };

    const saveSettings = async () => {
        if (!editingConfig) return;
        try {
            await apiService.makeRequest(`/organic-seo/maintenance/config/${editingConfig.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    is_active: editingConfig.is_active,
                    batch_size: parseInt(editBatchSize, 10)
                })
            });
            setEditingConfig(null);
            fetchConfigs();
        } catch (error) {
            console.error("Error updating settings", error);
        }
    };

    const getJobIcon = (type) => {
        if (type === 'broken_links') return <ShieldCheck className="w-8 h-8 text-blue-400" />;
        if (type === 'yoast_meta') return <Database className="w-8 h-8 text-orange-400" />;
        return <Activity className="w-8 h-8 text-gray-400" />;
    };

    const formatJobName = (type) => {
        if (type === 'broken_links') return 'Broken Links Fixer';
        if (type === 'yoast_meta') return 'Yoast Meta Optimizer';
        return type;
    };

    if (loading) return <div className="p-4 text-center text-gray-500">Loading Configuration...</div>;

    const yoastConfig = configs.find(c => c.job_type === 'yoast_meta');
    const seoScore = yoastConfig && yoastConfig.total_posts > 0 
        ? Math.round(((yoastConfig.processed_posts || 0) / yoastConfig.total_posts) * 100) 
        : (yoastConfig && yoastConfig.total_posts === 0 ? 100 : 0);

    let scoreColor = "text-red-500";
    if (seoScore >= 50) scoreColor = "text-yellow-500";
    if (seoScore >= 80) scoreColor = "text-green-500";

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8 w-full">
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800">
                        <Activity className="text-purple-600" />
                        SEO Health Automations
                    </h2>
                    <p className="text-sm text-gray-500">Nightly background jobs to autonomously scan and heal your WordPress domain.</p>
                </div>
                
                {yoastConfig && (
                    <div className="flex items-center gap-4 bg-gray-50 px-6 py-3 rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex flex-col text-right">
                            <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">Site SEO Rating</span>
                            <span className="text-[10px] text-gray-400">Title & Meta Optimization</span>
                        </div>
                        <div className={`text-4xl font-black ${scoreColor} drop-shadow-sm`}>
                            {seoScore}%
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {configs.map(cfg => (
                    <div key={cfg.id} className="bg-gray-50 p-5 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex gap-4">
                                <div className="p-3 bg-white border border-gray-100 rounded-lg shadow-sm">
                                    {getJobIcon(cfg.job_type)}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800">{formatJobName(cfg.job_type)}</h3>
                                    <p className="text-xs text-gray-500 truncate w-32" title={cfg.domain_url}>{cfg.domain_url.replace('https://', '').replace('www.', '')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {cfg.status === 'running' ? (
                                    <button 
                                        onClick={() => stopJob(cfg)}
                                        className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-red-200"
                                    >
                                        <Pause className="w-3 h-3" /> STOP
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => startJob(cfg)}
                                        disabled={startingJobId === cfg.id}
                                        className={`px-3 py-1 border rounded-lg text-xs font-bold flex items-center gap-1 ${startingJobId === cfg.id ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed' : 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200'}`}
                                    >
                                        <Play className="w-3 h-3" /> {startingJobId === cfg.id ? 'STARTING...' : 'START'}
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className="flex justify-between items-center text-sm text-gray-600 bg-white border border-gray-200 p-3 rounded-md mb-4 shadow-sm">
                            <div className="flex flex-col w-full">
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-700">Progress</span>
                                    <span className="text-xs font-medium text-gray-700">{cfg.processed_posts || 0} / {cfg.total_posts || 0}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                    <div 
                                        className={`h-2 rounded-full ${cfg.status === 'running' ? 'bg-purple-600 animate-pulse' : 'bg-green-500'}`} 
                                        style={{ width: `${cfg.total_posts > 0 ? Math.min(100, ((cfg.processed_posts || 0) / cfg.total_posts) * 100) : 0}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <div className="flex flex-col">
                                        <span className="text-xs uppercase text-gray-400">Status</span>
                                        <span className="text-xs font-bold capitalize text-gray-600">{cfg.status || 'idle'}</span>
                                    </div>
                                    <div className="flex flex-col text-right">
                                        <span className="text-xs uppercase text-gray-400">Batch Limit</span>
                                        <span className="text-gray-800 font-mono text-xs">{cfg.batch_size} posts/run</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                             <button onClick={() => openReport(cfg.id)} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium bg-indigo-50 px-3 py-1.5 rounded-md border border-indigo-100 transition">
                                 <FileText className="w-4 h-4" /> 📝 Katifi Report
                             </button>
                             <button onClick={() => { setEditingConfig(cfg); setEditBatchSize(cfg.batch_size); }} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 font-medium bg-purple-50 px-3 py-1.5 rounded-md border border-purple-100 transition">
                                 <Settings className="w-4 h-4" /> Configure
                             </button>
                        </div>
                    </div>
                ))}

                {configs.length === 0 && (
                    <div className="col-span-full text-sm text-gray-500 text-center py-6">No connected domains or configurations found. Connect a domain first.</div>
                )}
            </div>

            {/* Edit Modal */}
            {editingConfig && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-xl w-full max-w-md border border-gray-200 shadow-xl">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">Configure {formatJobName(editingConfig.job_type)}</h3>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-600 mb-2">Limit / Batch Size</label>
                            <input 
                                type="number" 
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-gray-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                                value={editBatchSize}
                                onChange={(e) => setEditBatchSize(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-2">Maximum number of WordPress posts to scan/process during the nightly cron execution to prevent server overload.</p>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setEditingConfig(null)} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50">Cancel</button>
                            <button onClick={saveSettings} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 shadow-sm">Save Configuration</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Katifi Report Modal */}
            {reportConfigId && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                            <h3 className="text-xl font-bold flex items-center gap-2 text-indigo-800">
                                <Activity className="text-indigo-600" />
                                Katifi AI SEO Analysis
                            </h3>
                            <button onClick={() => { setReportConfigId(null); setKatifiReport(null); }} className="text-gray-400 hover:text-gray-600 p-1 bg-white border rounded">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1 bg-white">
                            {loadingReport ? (
                                <div className="flex flex-col items-center justify-center h-64">
                                    <Activity className="w-12 h-12 text-indigo-500 animate-pulse mb-4" />
                                    <p className="text-indigo-600 font-bold text-lg">Katifi is analyzing your domain history...</p>
                                    <p className="text-gray-500 text-sm mt-2">Checking metadata and page structures.</p>
                                </div>
                            ) : katifiReport ? (
                                <div>
                                    <div className="mb-8 p-6 bg-indigo-50/50 border border-indigo-100 rounded-xl">
                                        <h4 className="font-bold text-lg text-indigo-900 mb-4 flex items-center gap-2">
                                            ✨ Executive Summary
                                        </h4>
                                        <div className="text-gray-700 space-y-3 leading-relaxed text-sm"
                                             dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(katifiReport.katifi_analysis
                                                 .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                 .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                 .replace(/\n\n/g, '</p><p>')
                                                 .replace(/\n/g, '<br/>')) }}
                                        />
                                    </div>
                                    
                                    <h4 className="font-bold text-md text-gray-800 mb-3 flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-gray-500" />
                                        Modification History (Latest {katifiReport.logs?.length || 0})
                                    </h4>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-gray-50 text-gray-600 border-b">
                                                <tr>
                                                    <th className="px-4 py-3 font-semibold">Date</th>
                                                    <th className="px-4 py-3 font-semibold">Action Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {katifiReport.logs && katifiReport.logs.length > 0 ? (
                                                    katifiReport.logs.map((log, idx) => (
                                                        <tr key={idx} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                                                            <td className="px-4 py-4 whitespace-nowrap text-gray-500 font-mono text-xs align-top">{log.date}</td>
                                                            <td className="px-4 py-4">
                                                                <div className="font-semibold text-gray-800 mb-1">{log.title}</div>
                                                                <div className="text-xs text-blue-600 bg-blue-50 py-1 border-l-2 border-blue-400 pl-2 leading-relaxed">{log.action}</div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="2" className="px-4 py-12 text-center text-gray-500">No recent automated SEO activity found on this config.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-10 text-red-500 font-medium">Failed to load report.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeoHealthConsole;
